import { useState, useRef, useCallback, useEffect } from 'react';
import html2canvas from 'html2canvas';

const STORAGE_KEY_PREFIX = 'gv_lobby_screenshot_';
const DEBOUNCE_MS = 3_000; // 3 seconds

// ── localStorage helpers ──────────────────────────────────────────────────────
function readCache(lobbyId) {
  try { return localStorage.getItem(STORAGE_KEY_PREFIX + lobbyId) || null; }
  catch { return null; }
}

function writeCache(lobbyId, dataUrl) {
  try { localStorage.setItem(STORAGE_KEY_PREFIX + lobbyId, dataUrl); }
  catch { /* quota exceeded — ignore */ }
}

function deleteCache(lobbyId) {
  try { localStorage.removeItem(STORAGE_KEY_PREFIX + lobbyId); }
  catch { /* ignore */ }
}

// ── Hook ──────────────────────────────────────────────────────────────────────
/**
 * useLobbyScreenshots
 *
 * @param {React.RefObject} canvasRef         - ref on the builder canvas wrapper
 * @param {React.RefObject} activeLobbyIdRef  - always-current ref of the active lobby ID
 * @param {string[]}        lobbyIds          - all lobby IDs (to pre-load cache on mount)
 * @param {function}        onEnsureSlide0    - called before capture on a carousel; should
 *                                             navigate to slide 0 and resolve when done
 * @param {function}        getIsCarousel     - () => bool, whether the active lobby is carousel
 */
export function useLobbyScreenshots(
  canvasRef,
  activeLobbyIdRef,
  lobbyIds,
  onEnsureSlide0,
  getIsCarousel,
) {
  const [screenshots, setScreenshots] = useState({});
  const debounceTimers = useRef({});  // lobbyId → timer id
  const capturingRef   = useRef(false);

  // ── Pre-load cached screenshots on mount / lobby list change ─────────────
  useEffect(() => {
    if (!lobbyIds?.length) return;
    setScreenshots(prev => {
      const next = { ...prev };
      lobbyIds.forEach(id => {
        if (!next[id]) {
          const cached = readCache(id);
          if (cached) next[id] = cached;
        }
      });
      return next;
    });
  }, [lobbyIds?.join(',')]); // eslint-disable-line

  // ── Core capture ─────────────────────────────────────────────────────────
  const capture = useCallback(async (lobbyId) => {
    if (!lobbyId || !canvasRef.current || capturingRef.current) return;

    // Guard: only shoot if this lobby is still the active one in the canvas.
    // activeLobbyIdRef.current is the live value — no stale closure risk.
    if (activeLobbyIdRef.current !== lobbyId) {
      console.debug(`[screenshots] skipping capture for ${lobbyId} — canvas now shows ${activeLobbyIdRef.current}`);
      return;
    }

    // For carousel lobbies, navigate to slide 0 before shooting so the
    // thumbnail always shows the first slide regardless of where the user is.
    // if (getIsCarousel?.()) {
    //   await onEnsureSlide0?.();
    //   // One final rAF to ensure the browser has composited the new frame
    //   await new Promise(r => requestAnimationFrame(r));
    // }

    // Guard again — user may have switched while we waited for slide 0
    if (activeLobbyIdRef.current !== lobbyId) return;

    try {
      capturingRef.current = true;
      const target = canvasRef.current.querySelector('[data-canvas-box]') || canvasRef.current;

      const canvasEl = await html2canvas(target, {
        useCORS: true,
        allowTaint: false,
        scale: 0.5,
        logging: false,
        backgroundColor: '#1a2540',
        ignoreElements: el =>
          el.hasAttribute('data-no-screenshot') ||
          el.classList?.contains('MuiTooltip-popper') ||
          el.classList?.contains('MuiPopover-root'),
      });

      // Final guard — paranoia check after the async html2canvas work
      if (activeLobbyIdRef.current !== lobbyId) return;

      const dataUrl = canvasEl.toDataURL('image/jpeg', 0.7);
      setScreenshots(prev => ({ ...prev, [lobbyId]: dataUrl }));
      writeCache(lobbyId, dataUrl);
    } catch (err) {
      console.warn('[useLobbyScreenshots] capture failed:', err);
    } finally {
      capturingRef.current = false;
    }
  }, [canvasRef, activeLobbyIdRef, onEnsureSlide0, getIsCarousel]);

  // ── Debounced schedule (30s after last edit) ──────────────────────────────
  const scheduleCapture = useCallback((lobbyId) => {
    if (!lobbyId) return;
    if (debounceTimers.current[lobbyId]) clearTimeout(debounceTimers.current[lobbyId]);
    debounceTimers.current[lobbyId] = setTimeout(() => capture(lobbyId), DEBOUNCE_MS);
  }, [capture]);

  // ── Immediate capture (new lobby, switch to lobby with no preview) ────────
  const captureNow = useCallback((lobbyId) => {
    if (!lobbyId) return;
    if (debounceTimers.current[lobbyId]) clearTimeout(debounceTimers.current[lobbyId]);
    // 1.5s delay to let React finish painting the canvas
    debounceTimers.current[lobbyId] = setTimeout(() => capture(lobbyId), 1500);
  }, [capture]);

  // ── Delete (lobby removed) ────────────────────────────────────────────────
  const deleteScreenshot = useCallback((lobbyId) => {
    if (debounceTimers.current[lobbyId]) {
      clearTimeout(debounceTimers.current[lobbyId]);
      delete debounceTimers.current[lobbyId];
    }
    deleteCache(lobbyId);
    setScreenshots(prev => { const n = { ...prev }; delete n[lobbyId]; return n; });
  }, []);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => () => {
    Object.values(debounceTimers.current).forEach(clearTimeout);
  }, []);

  return { screenshots, scheduleCapture, captureNow, deleteScreenshot };
}